/**
 * Unified SEO and canonical helpers for listing pages (/priestor/*).
 * Shared across:
 * - Vite frontend (ListingDetail.tsx, Seo.tsx, ImageGallery.tsx)
 * - Prerender script (scripts/prerender-listings.mjs)
 * - Cloudflare CSP Worker (cloudflare-worker-csp.js)
 */

export function toListingSlug(id) {
  const normalized = String(id ?? '').trim();
  if (!normalized) return '';
  return encodeURIComponent(normalized);
}

/**
 * Returns the canonical slug for a listing.
 * Strips legacy 'ai-' prefixes and prefers row.slug over row.id.
 */
export function canonicalListingSlug(row) {
  const slugOrId = row?.slug || row?.id || '';
  const clean = slugOrId.startsWith('ai-') ? slugOrId.replace(/^ai-/, '') : slugOrId;
  return toListingSlug(clean);
}

export const CATEGORY_HUMAN_NAMES = {
  'Meeting/konferenčka': 'konferenčný priestor',
  'Meeting / konferenčka': 'konferenčný priestor',
  'Event priestor': 'eventový priestor',
  'Unikátny priestor': 'priestor na akcie',
  'Foto/Video štúdio': 'fotoateliér',
  'Foto/video štúdio': 'fotoateliér',
  'Ateliér / dielňa': 'ateliér',
  'Ateliér/dielňa': 'ateliér',
  'Exteriér': 'exteriérový priestor',
  'Coworking': 'coworkingový priestor'
};

export function humanizeCategory(category) {
  if (!category) return 'priestor';
  const clean = String(category).trim();
  if (CATEGORY_HUMAN_NAMES[clean]) return CATEGORY_HUMAN_NAMES[clean];
  const lower = clean.toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_HUMAN_NAMES)) {
    if (key.toLowerCase() === lower) return val;
  }
  return clean.toLowerCase();
}

/**
/**
 * Extracts the primary city name from a location string (e.g. "Prešov - Sekčov, Slovensko" -> "Prešov").
 */
export function extractCleanCity(location) {
  if (!location || typeof location !== 'string') return '';
  const parts = location.split(',').map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    if (/\d/.test(part)) continue;
    let clean = part.replace(/^mestská časť\s+/i, '').replace(/^okres\s+/i, '').trim();
    if (!clean) continue;
    const lower = clean.toLowerCase();
    if (['slovensko', 'slovakia', 'slovak republic', 'sr', 'sk'].includes(lower)) continue;
    // Strip sub-district or borough like "Prešov - Sekčov" -> "Prešov"
    const dashParts = clean.split(/[-–/]/).map(s => s.trim()).filter(Boolean);
    if (dashParts.length > 0) return dashParts[0];
  }
  return '';
}

/**
 * Builds a consistent page title for a listing strictly <= 60 characters.
 * Template priority:
 * 1. {Názov}, {Mesto} – {typ} do {kapacita} hostí | Espacero
 * 2. {Názov}, {Mesto} – do {kapacita} hostí | Espacero (omit type if title would exceed 60)
 * 3. {Názov}, {Mesto} – {typ} do {kapacita} hostí (omit brand if still > 60)
 * 4. {Názov}, {Mesto} – do {kapacita} hostí (omit type & brand)
 * 5. {Názov}, {Mesto} | Espacero
 * 6. {Názov}, {Mesto}
 * - Do not repeat city if title already contains it (e.g. "Family Hall Prešov")
 */
export function buildListingTitle(title, location, category, maxGuests) {
  const cleanTitle = String(title || '').trim();
  if (!cleanTitle) return 'Priestor na prenájom | Espacero';

  const city = extractCleanCity(location);

  // If city is already in the title, don't duplicate it
  const normTitle = cleanTitle.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const normCity = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const cityInTitle = Boolean(
    normCity && (
      normTitle === normCity ||
      new RegExp(`(?:^|\\W)${normCity}(?:$|\\W)`).test(normTitle) ||
      normTitle.includes(normCity)
    )
  );

  const prefix = (city && !cityInTitle) ? `${cleanTitle}, ${city}` : cleanTitle;

  const type = category ? humanizeCategory(category) : '';
  const cap = (maxGuests !== undefined && maxGuests !== null && Number(maxGuests) > 0)
    ? `do ${maxGuests} hostí`
    : '';

  const middleFull = type && cap ? `${type} ${cap}` : (type || cap);
  const base = middleFull ? `${prefix} – ${middleFull}` : prefix;
  const fullWithBrand = `${base} | Espacero`;

  // 1. If full title with category, capacity and brand is <= 60 chars
  if (fullWithBrand.length <= 60) {
    return fullWithBrand;
  }

  // 2. If full title without brand is <= 60 chars
  if (base.length <= 60) {
    return base;
  }

  // 3. Title was > 60 chars even without brand (audit P3): omit type (category)
  const noTypeBase = cap ? `${prefix} – ${cap}` : prefix;
  const noTypeWithBrand = `${noTypeBase} | Espacero`;
  if (noTypeWithBrand.length <= 60) {
    return noTypeWithBrand;
  }
  if (noTypeBase.length <= 60) {
    return noTypeBase;
  }

  // 4. Fallback: try prefix with brand or prefix alone
  const prefixWithBrand = `${prefix} | Espacero`;
  if (prefixWithBrand.length <= 60) {
    return prefixWithBrand;
  }
  if (prefix.length <= 60) {
    return prefix;
  }

  // 5. If even prefix exceeds 60 chars, truncate cleanly at word boundary
  const sub = prefix.slice(0, 60);
  const lastSpace = sub.lastIndexOf(' ');
  return (lastSpace > 20 ? sub.slice(0, lastSpace) : sub).trim().replace(/[,;:\-\s]+$/, '');
}

/**
 * Builds a blog <title> strictly <= 60 characters ending in "| Espacero".
 * Supports explicit seoTitle (from blog.json) or gracefully falls back.
 */
export function buildBlogTitle(title, seoTitle) {
  const brand = ' | Espacero';
  if (seoTitle) {
    const cleanSeo = String(seoTitle).trim();
    if (cleanSeo.toLowerCase().endsWith('| espacero')) {
      return cleanSeo.length <= 60 ? cleanSeo : cleanSeo.slice(0, 60);
    }
    const withBrand = `${cleanSeo}${brand}`;
    return withBrand.length <= 60 ? withBrand : cleanSeo;
  }

  let clean = String(title || '').trim();
  if (!clean) return 'Blog | Espacero';
  if (clean.length + brand.length <= 60) {
    return `${clean}${brand}`;
  }
  if (clean.toLowerCase().endsWith('| espacero') && clean.length <= 60) {
    return clean;
  }

  // Handle common repetitive academic prefixes to preserve distinctive region/topic
  const strategicMatch = clean.match(/^Strategický prehľad a digitálna analýza eventového trhu v (.+)$/i);
  if (strategicMatch) {
    const candidate = `Eventový trh v ${strategicMatch[1].trim()}${brand}`;
    if (candidate.length <= 60) return candidate;
  }

  const maxTitleLen = 60 - brand.length;
  const sub = clean.slice(0, maxTitleLen);
  const lastSpace = sub.lastIndexOf(' ');
  let shortened = (lastSpace > 15 ? sub.slice(0, lastSpace) : sub).trim();
  // Strip trailing punctuation and dangling prepositions
  shortened = shortened
    .replace(/[,;:\-\s]+$/, '')
    .replace(/\s+(v|na|o|z|do|s|k|pre|po|pri|a|i|u|od|po|ako)$/i, '')
    .replace(/[,;:\-\s]+$/, '');
  return `${shortened}${brand}`;
}

/**
 * Builds a meta description ending in a complete sentence, strictly <= 155 chars,
 * without truncation markers like '…' or '...'.
 */
export function buildMetaDescription(text, maxLen = 155, minLen = 0) {
  let clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';

  if (minLen > 0 && clean.length < minLen) {
    const withoutDot = clean.replace(/[.!?]+$/, '');
    const suffix1 = ' – overené priestory na prenájom, priamy kontakt a rezervácie bez provízie na Espacero.';
    const suffix2 = ' – overený výber a priamy kontakt na majiteľa na Espacero.';
    const suffix3 = ' s priamym kontaktom na Espacero.';
    
    if ((withoutDot + suffix1).length <= maxLen && (withoutDot + suffix1).length >= minLen) {
      clean = withoutDot + suffix1;
    } else if ((withoutDot + suffix2).length <= maxLen && (withoutDot + suffix2).length >= minLen) {
      clean = withoutDot + suffix2;
    } else if ((withoutDot + suffix1).length <= maxLen) {
      clean = withoutDot + suffix1;
    } else if ((withoutDot + suffix2).length <= maxLen) {
      clean = withoutDot + suffix2;
    } else if ((withoutDot + suffix3).length <= maxLen) {
      clean = withoutDot + suffix3;
    }
  }

  if (clean.length <= maxLen && /[.!?]$/.test(clean)) return clean;
  if (clean.length <= maxLen && !/[.!?]$/.test(clean)) return `${clean}.`;

  // Search for the last full sentence ending within maxLen (excluding ordinal numbers like 13. storočia)
  const sub = clean.slice(0, maxLen);
  const sentenceMatches = [...sub.matchAll(/([^\d\s][.!?])\s+(?=[A-ZÁČĎÉÍĹĽŇÓÔŔŠŤÚÝŽ0-9])/g)];
  if (sentenceMatches.length > 0) {
    const last = sentenceMatches[sentenceMatches.length - 1];
    const endIdx = last.index + 1; // position of the punctuation character
    if (endIdx >= 60) {
      return sub.slice(0, endIdx + 1).trim();
    }
  }

  // If the sentence ending is too short or absent, break cleanly at a word boundary
  const wordBoundary = sub.lastIndexOf(' ');
  const cut = wordBoundary > 0 ? sub.slice(0, wordBoundary) : sub;
  const stripped = cut.replace(/[,;:\-\s]+$/, '');
  return `${stripped}.`;
}

/**
 * Checks whether an image source is a generic placeholder or graphic.
 */
export function isPlaceholderImage(src) {
  if (!src || typeof src !== 'string') return true;
  const s = src.toLowerCase();
  return (
    s.includes('external-gallery-placeholder') ||
    s.includes('1519741497674') ||
    s.includes('og-image') ||
    s.includes('og-default') ||
    s.includes('no-image') ||
    s.includes('no-photo') ||
    s.includes('camera-placeholder') ||
    s.includes('broken-image')
  );
}

/**
 * Filters listing images to only real photos (excluding text graphics and placeholders).
 * Converts relative paths to absolute URLs.
 */
export function filterRealImages(images) {
  let list = [];
  if (Array.isArray(images)) {
    list = images;
  } else if (typeof images === 'string') {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) list = parsed;
    } catch {}
  }

  return list
    .filter(img => typeof img === 'string' && img.trim() && !isPlaceholderImage(img) && !img.startsWith('blob:') && !img.startsWith('data:'))
    .map(img => {
      const trimmed = img.trim();
      if (trimmed.startsWith('http')) return trimmed;
      const path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
      return `https://upload.espacero.com/file/${path}`;
    });
}
